import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createSafeStorage } from "@/shared/lib/safeStorage";
import { getCurrentUserKey } from "./tokenService";

/** credit4u desync 관측을 JWT sub 별로 영속화해 다음 홈 진입에 재안내한다(ADR 260730-03). [닫기]·CS 이탈은 이번 실행만 억제하고 앱 재시작 시 다시 안내한다. clear 는 resync-pwd 성공·재동기화 잡 SUCCEEDED·renewal completed 에서만; renewal_recovery_queued 는 DB 미반영이라 clear 금지. 만료 이벤트 버스와 합치지 않는다 — markDesyncPending 이 멱등이며 surfacing 은 이 store 한 경로다. */
type Credit4uDesyncState = {
  /** 사용자(JWT sub)별 — desync 관측 후 복구 증거가 없는 상태인가. */
  pendingByUser: Record<string, boolean>;
  markDesyncPending: (userKey: string) => void;
  clearDesyncPending: (userKey: string) => void;
};

const useCredit4uDesyncStore = create<Credit4uDesyncState>()(
  persist(
    (set) => ({
      pendingByUser: {},

      markDesyncPending: (userKey) =>
        set((s) =>
          // 멱등 — 이미 pending 이면 새 객체를 만들지 않는다(불필요한 구독자 재렌더 차단).
          s.pendingByUser[userKey]
            ? s
            : { pendingByUser: { ...s.pendingByUser, [userKey]: true } },
        ),

      clearDesyncPending: (userKey) =>
        set((s) => {
          if (!s.pendingByUser[userKey]) return s;
          const next = { ...s.pendingByUser };
          delete next[userKey];
          return { pendingByUser: next };
        }),
    }),
    {
      name: "credit4u-desync",
      storage: createJSONStorage(createSafeStorage),
      version: 1,
      // 형식이 바뀌면 파싱 실패로 깨지는 대신 조용히 초기화한다.
      migrate: () => ({ pendingByUser: {} }) as Credit4uDesyncState,
      partialize: (s) =>
        ({ pendingByUser: s.pendingByUser }) as Credit4uDesyncState,
    },
  ),
);

export default useCredit4uDesyncStore;

// ────────────────────────────────────────────────────────────
// 현재 사용자 기준 헬퍼 — 키가 없으면(비로그인) 조용히 no-op / false
// ────────────────────────────────────────────────────────────

/** desync 관측 시 호출. 비로그인 상태면 no-op. */
export const markCredit4uDesyncPending = (): void => {
  const userKey = getCurrentUserKey();
  if (!userKey) return;
  useCredit4uDesyncStore.getState().markDesyncPending(userKey);
};

/** 복구 증거 확보 시 호출. 비로그인 상태면 no-op. pending 이 아니었으면 무해한 no-op. */
export const clearCredit4uDesyncPending = (): void => {
  const userKey = getCurrentUserKey();
  if (!userKey) return;
  useCredit4uDesyncStore.getState().clearDesyncPending(userKey);
};

// ────────────────────────────────────────────────────────────
// 런타임 억제 (persist 대상 아님 — 모듈 변수)
// ────────────────────────────────────────────────────────────

let suppressedForRuntime = false;

/**
 * 이번 앱 실행 동안 재안내를 접는다 — [닫기] · CS escalate 시 호출. 앱 재시작(리로드)으로 리셋.
 *
 * ⛔ **반응성 계약 — 모듈 변수는 React 가 구독할 수 없지만 그래도 충분하다.**
 * 억제가 바뀌는 모든 경로가 *동시에* 반응형 변화를 함께 일으키기 때문이다:
 *
 * | 억제를 켜는 주체 | 함께 일어나는 반응형 변화 | 호스트 재평가 계기 |
 * |---|---|---|
 * | 전역 호스트의 [닫기] | 호스트 자신의 로컬 setOpen(false) | 자기 핸들러라 직접 닫는다 |
 * | CreditPasswordRenewalPage 로컬 시트 [닫기] → 홈 | pathname 전환 | effect 재실행 |
 * | goHomeWithCsPopup() (resync 실패·임시비번) → 홈 | pathname 전환 | effect 재실행 |
 * | handleDesync() (RENEWAL_DESYNC) → 홈 | pathname 전환 | effect 재실행 |
 *
 * → 억제만 단독으로 바뀌면서 "이미 열린 시트를 닫아야 하는" 조합은 존재하지 않는다.
 *   따라서 이 값은 **"다음 open 판정에서 열지 않기" 전용**이며 store 로 승격하지 않는다.
 *
 * ⚠️ 억제 호출처를 새로 추가하는 사람은 위 표에 행을 더하고, 그 경로가 반응형 변화를 동반하는지
 *    확인할 것. 동반하지 않는 경로가 생기면 이 불변식이 깨진다.
 */
export const suppressCredit4uDesyncNotice = (): void => {
  suppressedForRuntime = true;
};

export const isCredit4uDesyncNoticeSuppressed = (): boolean =>
  suppressedForRuntime;
